import {
  ApolloClient,
  createHttpLink,
  ApolloLink,
  InMemoryCache,
  from,
} from "@apollo/client/core";
import { trace, SpanKind, SpanStatusCode } from "@opentelemetry/api";
import { getStore } from "@/store";
import { onError } from "@apollo/client/link/error";
import type { Exception } from "@opentelemetry/api";
import { omit } from "lodash";

const httpLink = createHttpLink({
  uri: process.env.VUE_APP_GRAPHQL_ENDPOINT,
  headers: {
    "x-api-key": process.env.VUE_APP_APPSYNC_ACCESS_KEY || "",
  },
});

// Log any GraphQL errors or network error that occurred
export const errorLink = onError(
  ({ graphQLErrors, networkError, operation }) => {
    const span = operation.getContext().span;

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        span.setStatus({ code: SpanStatusCode.ERROR });
        span.recordException({
          message: `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        } as Exception);
      });
      span.end();
    }

    if (networkError) {
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.recordException({
        message: `[Network error]: ${networkError}`,
      } as Exception);
      span.end();
    }
  }
);

// This handles configuring the JWT token as a Bearer auth header token for
// processing in the backend API. Will also log out users who have an expired
// token.
// NOTE: Because we return null, we are ultimately returning null to the caller
// as a response. Some calls when receiving this null will display naive-ui
// messages.
// Exported for testing reasons.
export const authMiddleware = new ApolloLink((operation, forward) => {
  const token = getStore().getters["user/getToken"];
  const validToken = getStore().getters["user/isLoggedIn"];

  // If all we are doing is sending Gemini events, don't remove / log out
  // the user. This can be useful for unnecessarily redirecting people on
  // static pages (such as the docs) to login when their token expires.
  // Either this middleware or the router guard will catch them later on
  // internal app pages.
  const operationName = operation.operationName;
  const isGeminiEvent = operationName === "SendGemini";

  if (token && !validToken && !isGeminiEvent) {
    getStore().dispatch("user/logUserOut");
    return null;
  }

  // May not have a valid token yet, such as for signup, login, etc.
  if (validToken) {
    const authorization = `Bearer ${token.replace(/\n/g, "")}`;
    operation.setContext(({ headers }: { headers: Record<string, any> }) => ({
      headers: {
        authorization,
        ...headers,
      },
    }));
  }
  return forward(operation);
});

//Creates a span with apollo data
export const createSpanLink = new ApolloLink((operation, forward) => {
  //scrubs out password if present
  const spanVars: Record<string, any> = omit(
    operation.variables.input,
    "password"
  );

  const tracer = trace.getTracer("@apollo/client");
  const span = tracer.startSpan(`gql.${operation.operationName}`, {
    startTime: operation.getContext().start,
    attributes: {
      target: operation.operationName,
      account_id: getStore().getters["user/getAccountId"] || "",
      user_id: getStore().getters["user/getId"] || "",
      app_group: getStore().getters["user/getAppgroupId"] || "",
      ...spanVars,
    },
    kind: SpanKind.INTERNAL,
  });

  operation.setContext({ span });

  return forward(operation).map((data) => {
    span.end();
    return data;
  });
});

const cache = new InMemoryCache();

const apolloClient = new ApolloClient({
  link: from([createSpanLink, errorLink, authMiddleware, httpLink]),
  cache,
  name: "scout-otel-api",
  version: process.env.VERSION,
  connectToDevTools: process.env.VUE_APP_BUILD_MODE === "devel",
});

export default apolloClient;
